import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "sidebar-inner",
  "data-testing": "sidebar-inner"
}
const _hoisted_2 = {
  key: 0,
  class: "sidebar-logo"
}
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "icon-close"
}
const _hoisted_5 = { class: "primary-links" }

import { mdiMenu, mdiBackburger } from "@mdi/js";
import { DefineComponent, computed } from "vue";
import { useRoute } from "vue-router";

import { USER } from "@/constants/user.constants";
import { useOrganizationStore } from "@/stores/organization.store";
import { useProductsStore } from "@/stores/products.store";
import { useUIGeneralStore } from "@/stores/ui-general.store";
import { useUsersStore } from "@/stores/users.store";

import { ProductSummaryItem } from "@/models/product.model";
import { SidebarNavigationLink } from "@/models/sidebar.model";

import { useCmsAccess } from "@/composables/cms-access";
import { useTasksQueryManager } from "@/composables/tasks-query-manager";

import { getProductsForDisplay } from "@/utils/filter-products-helpers";

import CmsIcon from "@/components/sidebar/icons/CmsIcon.vue";
import CurriculumIcon from "@/components/sidebar/icons/CurriculumIcon.vue";
import FilesIcon from "@/components/sidebar/icons/FilesIcon.vue";
import HomeIcon from "@/components/sidebar/icons/HomeIcon.vue";
import LogsIcon from "@/components/sidebar/icons/LogsIcon.vue";
import ProductsIcon from "@/components/sidebar/icons/ProductsIcon.vue";
import ProgrammesIcon from "@/components/sidebar/icons/ProgrammesIcon.vue";
import TasksIcon from "@/components/sidebar/icons/TasksIcon.vue";
import TeamIcon from "@/components/sidebar/icons/TeamIcon.vue";
import SidebarLink from "@/components/sidebar/SidebarLink.vue";
import SidebarPrograms from "@/components/sidebar/SidebarPrograms.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'Sidebar',
  setup(__props) {

const uiGeneralStore = useUIGeneralStore();
const orgStore = useOrganizationStore();
const usersStore = useUsersStore();
const productsStore = useProductsStore();
const route = useRoute();
const { getTasksQuery } = useTasksQueryManager();
const { visibleCms } = useCmsAccess();

const views = {
  home: ["home"],
  tasks: ["tasks"],
  programmes: [
    "programmes",
    "products",
    "createProgramme",
    "createProductInProgramme",
    "createProduct",
    "product",
    "content",
    "content-templates",
    "assets",
    "progress",
    "productTasks",
    "planning",
    "product-permissions",
    "feature-management",
    "html-editor",
    "html-preview",
    "content-extractor"
  ],
  products: ["all-products"],
  users: ["organizations", "createUser", "users"],
  files: ["files"],
  curriculums: ["curriculums", "createCurriculum", "curriculum"],
  cms: ["cms"],
  logs: ["logs"]
};

const productsForDisplay = computed<ProductSummaryItem[]>(() => {
  return usersStore.currentUser && productsStore.productSummary
    ? getProductsForDisplay(
        productsStore.productSummary,
        usersStore.currentUser.permissions
      )
    : [];
});
const isCurrentRouteIncluded = (namesArray: string[]) => {
  return (
    !!route.name &&
    typeof route.name === "string" &&
    namesArray.includes(route.name)
  );
};
const canUserAccessLogs = computed<boolean>(() => {
  return (
    orgStore.canOrgManageLogs &&
    !!usersStore.currentUser &&
    USER.isInternalUser(usersStore.currentUser.type ?? 0)
  );
});
const links = computed<SidebarNavigationLink[]>(() => {
  const allLinks: SidebarNavigationLink[] = [
    {
      title: "Home",
      isHighlighted: isCurrentRouteIncluded(views.home),
      to: {
        name: "home",
        query: getTasksQuery() as unknown as Record<string, string>
      },
      icon: HomeIcon as unknown as DefineComponent
    },
    {
      title: "Tasks",
      dataTesting: "sidebar-tasks",
      isHighlighted: isCurrentRouteIncluded(views.tasks),
      isNotAvailable:
        !orgStore.canOrgManageProgrammes || usersStore.areUsersLoading,
      to: {
        name: "tasks",
        query: getTasksQuery() as unknown as Record<string, string>
      },
      icon: TasksIcon as unknown as DefineComponent
    },
    {
      title: "Products",
      dataTesting: "sidebar-products",
      isHighlighted: isCurrentRouteIncluded(views.products),
      isNotAvailable: productsForDisplay.value.length === 0,
      to: {
        name: "all-products"
      },
      icon: ProductsIcon as unknown as DefineComponent
    },
    {
      title: "Programmes",
      dataTesting: "sidebar-programmes",
      isHighlighted: isCurrentRouteIncluded(views.programmes),
      isNotAvailable: !orgStore.canOrgManageProgrammes,
      to: {
        name: "programmes"
      },
      icon: ProgrammesIcon as unknown as DefineComponent
    },
    {
      title: "Curriculums",
      dataTesting: "sidebar-curriculums",
      isHighlighted: isCurrentRouteIncluded(views.curriculums),
      isNotAvailable: !orgStore.canOrgManageCurriculums,
      to: {
        name: "curriculums"
      },
      icon: CurriculumIcon as unknown as DefineComponent
    },
    {
      title: "Attachments",
      dataTesting: "sidebar-files",
      isHighlighted: isCurrentRouteIncluded(views.files),
      isNotAvailable: !orgStore.canOrgManageProgrammes,
      to: {
        name: "files"
      },
      icon: FilesIcon as unknown as DefineComponent
    },
    {
      title: "Users",
      dataTesting: "sidebar-manage-permissions",
      isHighlighted: isCurrentRouteIncluded(views.users),
      to: {
        name: "users"
      },
      icon: TeamIcon as unknown as DefineComponent
    },
    {
      title: "CMS",
      dataTesting: "sidebar-cms",
      isHighlighted: isCurrentRouteIncluded(views.cms),
      isNotAvailable:
        !orgStore.canOrgManageCms || visibleCms.value.length === 0,
      to: {
        name: "cms"
      },
      icon: CmsIcon as unknown as DefineComponent
    },
    {
      title: "Logs",
      dataTesting: "sidebar-logs",
      isHighlighted: isCurrentRouteIncluded(views.logs),
      isNotAvailable: !canUserAccessLogs.value,
      to: {
        name: "logs"
      },
      icon: LogsIcon as unknown as DefineComponent
    }
  ];

  return allLinks;
});

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["sidebar", { open: _unref(uiGeneralStore).isSidebarOpen }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h2", {
        class: _normalizeClass([{ 'padding-standard': _unref(uiGeneralStore).isSidebarOpen }, "lineheight-1 sidebar-header"])
      }, [
        (_unref(uiGeneralStore).isSidebarOpen)
          ? (_openBlock(), _createElementBlock("span", _hoisted_2, "THOTH"))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(uiGeneralStore).toggleSidebarWidth())),
          class: "btn-unstyled btn-toggle",
          "data-testing": "sidebar-hamburger"
        }, [
          (_unref(uiGeneralStore).isSidebarOpen)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                _createVNode(_component_SVGIcon, {
                  path: _unref(mdiBackburger),
                  size: 24
                }, null, 8, ["path"])
              ]))
            : (_openBlock(), _createElementBlock("span", _hoisted_4, [
                _createVNode(_component_SVGIcon, {
                  path: _unref(mdiMenu),
                  size: 24
                }, null, 8, ["path"])
              ]))
        ])
      ], 2),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(links.value, (link) => {
          return (_openBlock(), _createBlock(SidebarLink, {
            link: link,
            key: link.title
          }, {
            default: _withCtx(() => [
              (_openBlock(), _createBlock(_resolveDynamicComponent(link.icon)))
            ]),
            _: 2
          }, 1032, ["link"]))
        }), 128))
      ]),
      (_unref(uiGeneralStore).isSidebarOpen)
        ? (_openBlock(), _createBlock(SidebarPrograms, { key: 0 }))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})